import { REMINDER_SORT_OPTIONS } from './filterConstants';
import { REMINDER_TYPES, REMINDER_STATUSES } from './constants';

/**
 * Local wall-clock ISO-8601 with offset, e.g. 2026-09-05T10:00:00+05:30.
 * Import confirm must not send UTC `toISOString()` (`Z`).
 */
export function formatDateTimeOffset(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const pad = (n) => String(Math.abs(n)).padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const offsetMin = -date.getTimezoneOffset();
  const sign = offsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  const offH = pad(Math.floor(abs / 60));
  const offM = pad(abs % 60);
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offH}:${offM}`;
}

export function formatRemindAt(dateString) {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function getDuplicateRowKey(row, index) {
  const id = row?.id ?? row?.existing_reminder_id ?? row?.reminder_id ?? '';
  const phone = row?.phone ?? '';
  return `${id}:${phone}:${index}`;
}

export function getSelectedDuplicatePhones(duplicates, selectedKeys) {
  const selected = new Set(selectedKeys);
  const phones = [];
  const seen = new Set();
  (duplicates || []).forEach((row, index) => {
    if (!selected.has(getDuplicateRowKey(row, index))) return;
    const phone = row?.phone;
    if (!phone || seen.has(phone)) return;
    seen.add(phone);
    phones.push(phone);
  });
  return phones;
}

export function getReminderTypeLabel(type) {
  return REMINDER_TYPES[type]?.label || type || '-';
}

export function getReminderTypeTone(type) {
  return REMINDER_TYPES[type]?.tone || 'neutral';
}

export function getReminderStatusMeta(status) {
  const meta = REMINDER_STATUSES[status];
  return meta
    ? { label: meta.label, tone: meta.tone }
    : { label: status || '-', tone: 'neutral' };
}

export function getReminderProgress(job) {
  if (!job?.total_rows) return { percent: 0, label: '0%', done: 0, total: 0 };
  const done = (job.created_count || 0) + (job.skipped_count || 0);
  const percent = Math.min(100, Math.round((done / job.total_rows) * 100));
  return { percent, label: `${percent}%`, done, total: job.total_rows };
}

export function buildRemindersQueryParams(filters) {
  const params = {
    page: filters.page || 1,
    page_size: filters.pageSize || 10,
  };

  if (filters.sortKey) {
    const sortOption = REMINDER_SORT_OPTIONS.find(
      (o) => o.value === filters.sortKey,
    );
    if (sortOption?.ordering) params.ordering = sortOption.ordering;
  }

  if (filters.typeFilter) {
    params.types = filters.typeFilter;
  }

  if (filters.statusFilter) {
    const preset = ['today', 'tomorrow', 'this_week'].includes(
      filters.statusFilter,
    )
      ? applyDatePreset(filters.statusFilter)
      : null;

    if (preset) {
      if (preset.fromDate) params.from_date = preset.fromDate;
      if (preset.toDate) params.to_date = preset.toDate;
      if (preset.status?.length) params.status = preset.status.join(',');
    } else {
      params.status = filters.statusFilter;
    }
  }

  if (filters.fromDate) {
    params.from_date = toIsoStart(filters.fromDate);
  }
  if (filters.toDate) {
    params.to_date = toIsoEnd(filters.toDate);
  }

  if (filters.contactId) params.contact_id = filters.contactId;
  if (filters.assignedEmployeeId) {
    params.assigned_employee_id = filters.assignedEmployeeId;
  }
  if (filters.search?.trim()) params.search = filters.search.trim();

  return params;
}

export function toIsoStart(date) {
  if (!date) return '';
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export function toIsoEnd(date) {
  if (!date) return '';
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

export function applyDatePreset(preset) {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  if (preset === 'today') {
    return {
      fromDate: toIsoStart(start),
      toDate: toIsoEnd(end),
      status: ['upcoming', 'overdue'],
    };
  }
  if (preset === 'tomorrow') {
    start.setDate(start.getDate() + 1);
    end.setDate(end.getDate() + 1);
    return {
      fromDate: toIsoStart(start),
      toDate: toIsoEnd(end),
      status: ['upcoming'],
    };
  }
  if (preset === 'this_week') {
    const day = now.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    start.setDate(now.getDate() + diff);
    end.setDate(start.getDate() + 6);
    return {
      fromDate: toIsoStart(start),
      toDate: toIsoEnd(end),
      status: ['upcoming', 'overdue'],
    };
  }
  return {};
}

export function getRowActionPath(reminder) {
  const contactId = reminder.contact?.id;
  if (!contactId) return null;

  switch (reminder.type) {
    case 'task':
      return `/crm-contacts/overview?contact_id=${contactId}&tab=tasks`;
    case 'follow_up':
      return `/crm-contacts/overview?contact_id=${contactId}&tab=follow-ups`;
    case 'appointment':
      return '/calendar';
    case 'scheduled_message':
      return `/crm-contacts/overview?contact_id=${contactId}`;
    default:
      return `/crm-contacts/overview?contact_id=${contactId}`;
  }
}
